import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, Copy, Loader2, Mail, Plus, Store, Users, Video, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import useAuthStore from '@/stores/authStore';
import MeetingRoom from '@/components/meetings/MeetingRoom';
import meetingsService, { parseEmails, type Meeting, type RtcAccess } from '@/services/meetings.service';

interface ActiveRoom {
  rtc: RtcAccess;
  displayName: string;
  meetingId: string;
  title: string | null;
  /** Raum-Besitzer? — nur er sieht/beantwortet Beitrittsanfragen. */
  mine: boolean;
}

/**
 * Meetings — Designsprache der KI-Entwürfe (Eyebrow, Pill-Summary,
 * Hairline-Zeilen, Seitentokens hell/dunkel).
 *
 * Erstellen fragt IMMER erst: Organisation (Berechtigungsgrenze — die
 * Einladungs-Mail geht über deren SMTP) und optional Desk-Kollegen
 * (treten ohne PIN bei). Nichts startet mehr „einfach so".
 */
export default function Meetings() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [room, setRoom] = useState<ActiveRoom | null>(null);
  /** Meet tarzı bekleme: kapı çalındı, sahibin onayı bekleniyor. */
  const [knocking, setKnocking] = useState<Meeting | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [orgId, setOrgId] = useState<string>('');
  const [memberIds, setMemberIds] = useState<Set<string>>(new Set());
  const [inviteFor, setInviteFor] = useState<Meeting | null>(null);
  const [inviteEmails, setInviteEmails] = useState('');
  const [guestEmails, setGuestEmails] = useState('');

  const { data: meetings, isLoading } = useQuery({
    queryKey: ['meetings'],
    queryFn: () => meetingsService.list(),
    refetchInterval: 15_000,
    enabled: !room,
  });

  const { data: orgs } = useQuery({
    queryKey: ['meeting-orgs'],
    queryFn: () => meetingsService.organizations(),
    staleTime: 5 * 60_000,
  });

  // Org vorauswählen, sobald die Liste da ist
  useEffect(() => {
    if (!orgId && orgs?.length) setOrgId(orgs[0].id);
  }, [orgs, orgId]);

  const { data: colleagues } = useQuery({
    queryKey: ['meeting-colleagues', orgId],
    queryFn: () => meetingsService.colleagues(orgId),
    enabled: Boolean(orgId) && createOpen,
  });

  // Von der Seitenleisten-Karte: joinId → direkt beitreten, create → Dialog auf
  const location = useLocation();
  const consumed = useRef(false);
  useEffect(() => {
    const s = location.state as any;
    if (consumed.current || !s) return;
    consumed.current = true;
    if (s.joinId) void join({ id: s.joinId } as Meeting);
    if (s.create) setCreateOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['meetings'] });

  const createMutation = useMutation({
    mutationFn: () => meetingsService.create({
      title: title.trim() || undefined,
      organization_id: orgId,
      member_ids: [...memberIds],
      guest_emails: parseEmails(guestEmails),
    }),
    onSuccess: (m) => {
      setCreateOpen(false);
      setTitle('');
      setMemberIds(new Set());
      setGuestEmails('');
      if (m.invited?.sent.length) {
        toast.success(t('shell:meet.invitesSent', '{{count}} Einladung(en) gesendet', { count: m.invited.sent.length }));
      }
      if (m.invited?.failed.length) {
        toast.error(t('shell:meet.invitesFailed', '{{count}} Einladung(en) fehlgeschlagen', { count: m.invited.failed.length }));
      }
      invalidate();
      void join(m);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('errors:generic', 'Etwas ist schiefgelaufen')),
  });

  const inviteMutation = useMutation({
    mutationFn: () => meetingsService.invite(inviteFor!.id, parseEmails(inviteEmails)),
    onSuccess: (r) => {
      if (r.sent.length) toast.success(t('shell:meet.invitesSent', '{{count}} Einladung(en) gesendet', { count: r.sent.length }));
      if (r.failed.length) toast.error(t('shell:meet.invitesFailed', '{{count}} Einladung(en) fehlgeschlagen', { count: r.failed.length }));
      setInviteEmails(''); setInviteFor(null);
      invalidate();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('shell:meet.inviteFailed', 'Einladung fehlgeschlagen')),
  });

  const endMutation = useMutation({
    mutationFn: (id: string) => meetingsService.end(id),
    onSuccess: invalidate,
  });

  const join = async (m: Meeting) => {
    try {
      const access = await meetingsService.getToken(m.id);
      setKnocking(null);
      setRoom({
        rtc: access.rtc,
        displayName: access.displayName,
        meetingId: m.id,
        title: access.meeting.title,
        mine: m.created_by === user?.id,
      });
    } catch (e: any) {
      if (e?.response?.data?.code === 'NOT_MEMBER') {
        // Üye değiliz: istek bırak, onay gelene dek bekle (aşağıdaki effect poll'lar)
        try { await meetingsService.requestJoin(m.id); } catch { /* noop */ }
        setKnocking(m);
        return;
      }
      toast.error(e?.response?.data?.message || t('shell:meet.joinFailed', 'Beitritt fehlgeschlagen'));
    }
  };

  // Kapı çalındıysa: 3 sn'de bir durum sor; approved → gir, denied → vazgeç
  useEffect(() => {
    if (!knocking) return;
    const id = window.setInterval(async () => {
      try {
        const st = await meetingsService.requestStatus(knocking.id);
        if (st.status === 'approved') {
          window.clearInterval(id);
          void join(knocking);
        } else if (st.status === 'denied') {
          window.clearInterval(id);
          setKnocking(null);
          toast.error(t('shell:meet.joinDenied', 'Beitritt wurde abgelehnt'));
        }
      } catch { /* noop */ }
    }, 3000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knocking?.id]);

  const copyLink = (m: Meeting) => {
    const link = m.guest_link || `${window.location.origin}/meet/${m.guest_code}`;
    void navigator.clipboard.writeText(link);
    toast.success(t('shell:meet.linkCopied', 'Link kopiert — Zutritt nur mit E-Mail-Einladung (PIN)'));
  };

  if (room) {
    return (
      <div className="h-full">
        <MeetingRoom
          rtc={room.rtc}
          displayName={room.displayName}
          title={room.title}
          onInvite={async (emails) => {
            const r = await meetingsService.invite(room.meetingId, emails);
            return r.sent.length;
          }}
          onHeartbeat={(count) => void meetingsService.heartbeat(room.meetingId, count).catch(() => {})}
          peers={{ get: () => meetingsService.peers(room.meetingId) }}
          requests={room.mine ? {
            get: () => meetingsService.joinRequests(room.meetingId),
            decide: (reqId, decision) => meetingsService.decideRequest(room.meetingId, reqId, decision),
          } : undefined}
          captionsControl={{
            get: () => meetingsService.captionsState(room.meetingId),
            start: (source, targets) => meetingsService.captionsStart(room.meetingId, { source, targets }),
            stop: () => meetingsService.captionsStop(room.meetingId),
          }}
          onLeave={() => { setRoom(null); invalidate(); }}
        />
      </div>
    );
  }

  const invitedForMe = (meetings || []).filter((m) => m.is_invited);
  const others = (meetings || []).filter((m) => !m.is_invited);
  const ordered = [...invitedForMe, ...others];

  return (
    <div className="meet-page">
      {knocking && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-sm rounded-xl bg-white p-6 text-center shadow-xl dark:bg-[#1d1f22]">
            <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin" style={{ color: 'var(--mp-muted, #64748b)' }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--mp-ink, #101319)' }}>
              {t('shell:meet.knocking', 'Beitritt angefragt')}
            </p>
            <p className="mt-1 text-xs" style={{ color: 'var(--mp-muted, #64748b)' }}>
              {t('shell:meet.knockingHint', 'Der Raum-Besitzer muss den Beitritt freigeben — einen Moment …')}
            </p>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => setKnocking(null)}>
              {t('common:action.cancel', 'Abbrechen')}
            </Button>
          </div>
        </div>
      )}
      <header className="meet-page-head">
        <div className="min-w-0">
          <div className="meet-page-eyebrow">{t('shell:meet.eyebrow', 'Video & Bildschirm')}</div>
          <h1>{t('shell:meet.title', 'Meetings')}</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="meet-page-summary max-sm:hidden">
            <span><strong>{meetings?.length ?? 0}</strong> {t('shell:meet.activeCount', 'aktiv')}</span>
            <span><strong>{invitedForMe.length}</strong> {t('shell:meet.forYou', 'für dich')}</span>
          </div>
          <button type="button" className="meet-new-btn" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('shell:meet.new', 'Neues Meeting')}
          </button>
        </div>
      </header>

      <div className="meet-page-body">
        {isLoading ? (
          <div className="flex justify-center py-14" style={{ color: 'var(--mp-muted)' }}>
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : !ordered.length ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center" style={{ color: 'var(--mp-muted)' }}>
            <Video className="h-6 w-6 opacity-60" />
            <strong className="text-sm" style={{ color: 'var(--mp-ink)' }}>{t('shell:meet.empty', 'Keine aktiven Meetings')}</strong>
            <span className="text-xs">{t('shell:meet.emptyHint', 'Starten Sie eines — Kollegen und Kunden lassen sich danach einladen.')}</span>
          </div>
        ) : (
          <div>
            {ordered.map((m) => {
              const host = [m.first_name, m.last_name].filter(Boolean).join(' ');
              const mine = m.created_by === user?.id;
              return (
                <div key={m.id} className={cn('meet-row', m.is_invited && 'is-invited')}>
                  <span className="meet-row-icon"><Video className="h-4 w-4" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      <p className="truncate text-[13px] font-semibold">{m.title || t('shell:meet.defaultTitle', 'Video-Gespräch')}</p>
                      {m.is_invited && <span className="meet-row-invited-badge">{t('shell:meet.invitedYou', 'Du bist eingeladen')}</span>}
                    </div>
                    <p className="mt-0.5 truncate text-[11px]" style={{ color: 'var(--mp-muted)' }}>
                      {(m.live_count ?? 0) > 0 && (
                        <span style={{ color: 'var(--mp-green)', fontWeight: 700 }}>
                          ● {m.live_count} {t('shell:meet.inCall', 'im Gespräch')} ·{' '}
                        </span>
                      )}
                      {host && `${host} · `}
                      {(m.members?.length ?? 0) > 0 && `${m.members!.slice(0, 3).map((x) => x.name).join(', ')} · `}
                      {(m.invite_count ?? 0) > 0 && `${m.invite_count} ${t('shell:meet.invited', 'eingeladen')}`}
                    </p>
                  </div>
                  {m.organization_name && <span className="meet-row-org max-sm:hidden"><Store className="mr-1 inline h-3 w-3" />{m.organization_name}</span>}
                  <div className="meet-row-actions flex items-center gap-1.5">
                    <button type="button" className="meet-icon-btn" title={t('shell:meet.copyLink', 'Link kopieren')} onClick={() => copyLink(m)}>
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <button type="button" className="meet-icon-btn" title={t('shell:meet.invite', 'Per E-Mail einladen')} onClick={() => setInviteFor(inviteFor?.id === m.id ? null : m)}>
                      <Mail className="h-3.5 w-3.5" />
                    </button>
                    {mine && (
                      <button type="button" className="meet-icon-btn" title={t('shell:meet.end', 'Beenden')} onClick={() => endMutation.mutate(m.id)}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button type="button" className="meet-join-btn" onClick={() => void join(m)}>
                      {t('shell:meet.join', 'Beitreten')}
                    </button>
                  </div>
                  {inviteFor?.id === m.id && (
                    <div className="mt-1 flex w-full flex-wrap items-center gap-2 pl-12 max-sm:pl-0">
                      <Input autoFocus value={inviteEmails} onChange={(e) => setInviteEmails(e.target.value)} placeholder={t('shell:meet.multiEmailPlaceholder', 'kunde@example.com, partner@firma.de …')} className="h-8 min-w-[260px] flex-1 text-sm" />
                      <Button size="sm" className="h-8" onClick={() => inviteMutation.mutate()} disabled={inviteMutation.isPending || parseEmails(inviteEmails).length === 0}>
                        {inviteMutation.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                        {t('shell:meet.sendInvite', 'Einladung senden')}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Erstellen — fragt zuerst: Organisation + Desk-Kollegen */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle>{t('shell:meet.new', 'Neues Meeting')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="meet-title">{t('shell:meet.titleLabel', 'Titel (optional)')}</Label>
              <Input id="meet-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('shell:meet.titlePlaceholder', 'z. B. „Beratung Hr. Müller“')} />
            </div>

            {(orgs?.length ?? 0) > 1 && (
              <div className="space-y-1.5">
                <Label>{t('shell:meet.orgLabel', 'Organisation (Shop)')}</Label>
                <Select value={orgId} onValueChange={(v) => { setOrgId(v); setMemberIds(new Set()); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(orgs || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {t('shell:meet.orgHint', 'Einladungs-E-Mails werden über diesen Shop versendet.')}
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                {t('shell:meet.inviteColleagues', 'Desk-Kollegen einladen (ohne PIN)')}
              </Label>
              <div className="max-h-44 space-y-1 overflow-y-auto rounded-xl border border-slate-200 p-2 dark:border-white/10">
                {(colleagues || []).filter((c) => c.id !== user?.id).map((c) => {
                  const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email;
                  const checked = memberIds.has(c.id);
                  return (
                    <label key={c.id} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-white/5">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          const next = new Set(memberIds);
                          if (v === true) next.add(c.id); else next.delete(c.id);
                          setMemberIds(next);
                        }}
                      />
                      <span className="min-w-0 flex-1 truncate">{name}</span>
                      <span className="text-[10px] uppercase text-slate-400">{c.role}</span>
                      {checked && <Check className="h-3.5 w-3.5 text-emerald-600" />}
                    </label>
                  );
                })}
                {!colleagues?.length && (
                  <p className="px-2 py-1.5 text-xs text-slate-500">
                    {t('shell:meet.noColleagues', 'Keine weiteren Desk-Nutzer in dieser Organisation.')}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="meet-guests" className="flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" />
              {t('shell:meet.externalGuests', 'Externe Gäste per E-Mail (mit PIN)')}
            </Label>
            <Input
              id="meet-guests"
              value={guestEmails}
              onChange={(e) => setGuestEmails(e.target.value)}
              placeholder={t('shell:meet.multiEmailPlaceholder', 'kunde@example.com, partner@firma.de …')}
            />
            <p className="text-xs text-slate-500">
              {parseEmails(guestEmails).length > 0
                ? t('shell:meet.emailsRecognized', '{{count}} Adresse(n) erkannt', { count: parseEmails(guestEmails).length })
                : t('shell:meet.multiEmailHint', 'Mehrere Adressen mit Komma oder Leerzeichen trennen.')}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>{t('common:action.cancel', 'Abbrechen')}</Button>
            <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending || !orgId}>
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('shell:meet.createAndJoin', 'Erstellen & beitreten')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
